import { useCallback, useState } from 'react';
import styles from './Main.module.css';
import * as XLSX from 'xlsx';
import { useDropzone } from 'react-dropzone';
import Upload from 'images/upload.svg';

const Main = () => {
  const [fileInfo, setFileInfo] = useState(''); // 단일 파일

  const LIMIT = 100;   // 한 번 API 요청 가능한 데이터 개수
  const MAX_LIMIT = 5000; // 최대 데이터 개수 (엑셀)

  // 엑셀 템플릿 컬럼 인덱스(0부터): 이미지 템플릿 기준 "사업자등록번호(필수)"가 6번째 열 → index 5
  const BIZ_NO_COL = 5;

  // 컬럼 값
  const HEADER_JSON = {
    b_no: '사업자등록번호',
    b_stt: '납세자상태(명칭)',
    b_stt_cd: '납세자상태(코드)',
    tax_type: '과세유형메세지(명칭)',
    tax_type_cd: '사업자등록번호(코드)',
    end_dt: '폐업일',
    utcc_yn: '단위과세전환폐업여부',
    tax_type_change_dt: '최근과세유형전환일자',
    invoice_apply_dt: '세금계산서적용일자',
    rbf_tax_type: '직전과세유형메세지(명칭)',
    rbf_tax_type_cd: '직전과세유형메세지(코드)',
  };

  // 상태값 코드
  const STATUS_CODE_JSON = [
    { codeType: '납세자상태', code: '01', desc: '계속사업자' },
    { codeType: '납세자상태', code: '02', desc: '휴업자' },
    { codeType: '납세자상태', code: '03', desc: '폐업자' },
    { codeType: '', code: '', desc: '' },
    { codeType: '과세유형메세지', code: '01', desc: '부가가치세 일반과세자' },
    { codeType: '과세유형메세지', code: '02', desc: '부가가치세 간이과세자' },
    { codeType: '과세유형메세지', code: '03', desc: '부가가치세 과세특례자' },
    { codeType: '과세유형메세지', code: '04', desc: '부가가치세 면세사업자' },
    { codeType: '과세유형메세지', code: '05', desc: '수익사업을 영위하지 않는 비영리법인이거나 고유번호가 부여된 단체,국가기관 등' },
    { codeType: '과세유형메세지', code: '06', desc: '고유번호가 부여된 단체' },
    { codeType: '과세유형메세지', code: '07', desc: '부가가치세 간이과세자(세금계산서 발급사업자)' },
    { codeType: '과세유형메세지', code: '', desc: "* 등록되지 않았거나 삭제된 경우: '국세청에 등록되지 않은 사업자등록번호입니다'" },
    { codeType: '', code: '', desc: '' },
    { codeType: '직전과세유형메세지', code: '01', desc: '부가가치세 일반과세자' },
    { codeType: '직전과세유형메세지', code: '02', desc: '부가가치세 간이과세자' },
    { codeType: '직전과세유형메세지', code: '07', desc: '부가가치세 간이과세자(세금계산서 발급사업자)' },
    { codeType: '직전과세유형메세지', code: '99', desc: '해당없음' },
  ];

  // 숫자/하이픈/공백 등 정규화: 숫자만 남김
  const normalizeBizNo = (v) => (v ? v.toString().replace(/\D/g, '') : '');

  // 일정 단위(size)로 배열을 나누는 함수
  const splitDataArray = (array, size) => {
    const result = [];
    for (let i = 0; i < array.length; i += size) {
      result.push(array.slice(i, i + size));
    }
    return result;
  };

  /**
   * 컬럼의 키 값에 한글명 매핑
   * @param originalData 업로드 데이터(원본 데이터) — 검증일자 포함(선두)
   * @param data API로 조회한 데이터 배열
   */
  const convertColumns = (originalData, data) => {
    const originalColumns = ['검증일자', '회원번호', '회원명', '회사명', '대표자명/이름', '프랜차이즈명', '사업자등록번호'];

    return data.map((item, index) => {
      const newItem = {};

      // 원본행(검증일자 포함)을 한글 헤더로 매핑
      originalData[index].forEach((value, i) => {
        newItem[originalColumns[i]] = value;
      });

      // API 결과 키 매핑
      for (const key in item) {
        if (Object.prototype.hasOwnProperty.call(HEADER_JSON, key)) {
          newItem[HEADER_JSON[key]] = item[key];
        } else {
          newItem[key] = item[key];
        }
      }

      return newItem;
    });
  };

  // 홈택스 사업자등록번호 상태조회 API
  const fetchGetStatus = async (bNos) => {
    try {
      // 환경변수에 이미 인코딩된 키가 있으면 그대로 사용, 없으면 인코딩
      const serviceKey = process.env.REACT_APP_DATA_OPEN_API_ENCODING_KEY || '';
      const URL =
          `${process.env.REACT_APP_DATA_OPEN_API_BASE_URL}/nts-businessman/v1/status` +
          `?serviceKey=${serviceKey}`;

      const resp = await fetch(URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
        },
        body: JSON.stringify({ b_no: bNos }),
      });

      if (!resp.ok) {
        // 서버가 200이 아니면 본문 로깅
        const text = await resp.text();
        throw new Error(`HTTP ${resp.status} - ${text}`);
      }

      const json = await resp.json();

      if (json.status_code !== 'OK') {
        throw new Error(json.status_code || 'API error');
      }

      return json; // { status_code:'OK', data:[...] }
    } catch (error) {
      console.error('fetchGetStatus Error:', error);
    }
  };

  // 전체 데이터 처리 (청크 전송)
  const allData = async (bizNos) => {
    if (bizNos.length > MAX_LIMIT) {
      alert('최대 5,000건까지 가능합니다.');
      return false;
    }

    const chunks = splitDataArray(bizNos, LIMIT);
    let allResult = [];

    for (let i = 0; i < chunks.length; i++) {
      const partial = await fetchGetStatus(chunks[i]);
      if (!partial || !partial.data) {
        console.warn('부분 응답이 비었습니다.', i);
        continue;
      }
      allResult = allResult.concat(partial.data);
    }

    return allResult;
  };

  /**
   * 엑셀 다운로드
   * @param originalData 업로드 데이터(검증일자 포함, 유효행만)
   * @param data API로 조회한 데이터
   */
  const createExcelFile = async (originalData, data) => {
    const workbook = XLSX.utils.book_new();
    const firstSheet = XLSX.utils.json_to_sheet(convertColumns(originalData, data)); // API 조회 결과 시트
    const secondSheet = XLSX.utils.json_to_sheet(STATUS_CODE_JSON); // 각 코드 값 설명 시트

    XLSX.utils.book_append_sheet(workbook, firstSheet, '상태조회 결과값');
    XLSX.utils.book_append_sheet(workbook, secondSheet, '코드값');

    const today = new Date().toISOString().split('T')[0];
    XLSX.writeFile(workbook, `${today}_사업자등록번호 상태조회.xlsx`);
  };

  // 엑셀파일 업로드 + API 조회 + 결과 다운로드
  const onDrop = useCallback((acceptedFiles) => {
        const file = acceptedFiles[0];
        const strFileInfo = `${file.path || file.name} (${(file.size
            / 1024).toFixed(2)} KB)`;
        setFileInfo(strFileInfo);

        const reader = new FileReader();
        reader.onload = async (event) => {
          const binaryStr = event.target?.result;

          const workbook = XLSX.read(binaryStr, {type: 'binary'});
          const sheetName = workbook.SheetNames[0];
          const sheet = workbook.Sheets[sheetName];

          // ★ 핵심: raw:false → 엑셀 표시 문자열로 읽기(과학표기 방지)
          const data = XLSX.utils.sheet_to_json(sheet, {
            header: 1,
            defval: '',
            raw: false,
          });

          // 헤더 제외한 원본 행들
          const bodyRows = data.slice(1);
          const today = new Date().toISOString().split('T')[0];

          // 유효한 10자리 사업자번호만 선별하면서, 동일한 인덱스의 원본 행도 같이 모은다.
          const validBizNos = [];
          const originalDataFiltered = [];

          let emptyCount = 0;
          let invalidCount = 0;

          for (const row of bodyRows) {
            const raw = (row[BIZ_NO_COL] ?? '').toString().trim();
            if (raw === '') {
              emptyCount++;
              continue;
            }
            const normalized = normalizeBizNo(raw); // 숫자만
            if (normalized.length === 10) {
              validBizNos.push(normalized);
              // 검증일자를 맨 앞 컬럼으로 추가
              originalDataFiltered.push([today, ...row]);
            } else {
              invalidCount++;
            }
          }

          if (emptyCount > 0 || invalidCount > 0) {
            alert(
                [
                  emptyCount > 0 ? `빈 사업자번호 ${emptyCount}건 제외` : null,
                  invalidCount > 0 ? `형식 오류(숫자 10자 아님) ${invalidCount}건 제외` : null,
                ]
                .filter(Boolean)
                .join('\n'),
            );
          }

          if (validBizNos.length === 0) {
            alert('유효한 사업자등록번호가 없습니다.');
            setFileInfo('');
            return;
          }

          // 청크 호출
          const result = await allData(validBizNos);

          if (result && Array.isArray(result)) {
            // API 결과 배열(result)와 originalDataFiltered는 1:1 순서대로 매칭됨
            await createExcelFile(originalDataFiltered, result);
          }

          setFileInfo('');
        };

        reader.readAsBinaryString(file);
      },
      []);

  const { getRootProps, getInputProps } = useDropzone({
    onDrop,
    accept: {
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
    },
    maxFiles: 1,
  });

  // 템플릿 다운로드
  const downloadTemplate = () => {
    const url = '/upload-template.xlsx';
    const a = document.createElement('a');
    a.href = url;
    a.download = 'upload-template.xlsx';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  return (
      <div className={styles['container']}>
        <div className={styles['wrap']}>
          <div className={styles['title']}>사업자등록번호 검증 조회</div>
          <div className={styles['attachment-wrap']}>
            <div className={styles['item-wrap']}>
              <label>템플릿 다운로드 링크</label>
              <div className={styles['link']}>
                <p onClick={downloadTemplate}>upload-template.xlsx</p>
              </div>
            </div>
            <div className={styles['item-wrap']}>
              <label>주의사항</label>
              <div className={styles['item-info']}>
                <p>1. 템플릿 다운로드 후 해당 템플릿에 작성 및 업로드해야합니다. (변경 X)</p>
                <p>
                  2. 사업자등록번호는 <b style={{ color: 'blue' }}>필수</b> 입니다. (10자리 or '-' 포함 12자리)
                </p>
                <p>3. 입력한 데이터가 많을 수록 시간이 오래 소요됩니다.</p>
                <p>4. 안되실 경우 화면 새로고침 후 시도 부탁드립니다.</p>
                <p>5. 1회당 5,000건 / 1일 1억건 조회 가능합니다.</p>
              </div>
            </div>
            <div className={styles['attachment']} {...getRootProps()}>
              <div className={styles['img']}>
                <img src={Upload} alt="upload" />
              </div>
              <p>첨부파일을 드래그 앤 드롭하거나 클릭하여 업로드 하세요.</p>
              <p>1개의 파일만 업로드 가능합니다.</p>
            </div>
            <div className={styles['file-name-wrap']}>
              <input {...getInputProps()} />
              {fileInfo}
            </div>
          </div>
        </div>
      </div>
  );
};

export default Main;